import { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useMutation, useQuery } from '@apollo/client';
// Api
import { TRIGGER_MATCHUP_SHARE_PICK_GRAPHIC } from 'api/matchup/mutations';
import { CHECK_GENERATION_JOB_STATUS } from 'api/matchup/queries';
// Types
import { MatchupCorner, InterviewImageStatus } from 'api/graphql-global-types';
// Helpers
import {
  Matchup,
  MatchupResults,
  getCompetitorDisplayName,
  getCornerCompetitor,
  getVoteBreakdown,
} from 'helpers/streamMatchups';
import { downloadImageFromUrl } from 'helpers/download';
// Ui
import Modal from 'ui3/Modal/Modal';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Loader from 'ui3/Loader/Loader';
import Text from 'ui3/Text/Text';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './SharePickModal.module.scss';

// Poll the image-generation job the same way the tip/interview posters do:
// every 2s, capped so a stuck job surfaces an error instead of spinning forever.
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30; // ~60s

type GenerationStatus = 'idle' | 'generating' | 'ready' | 'error';

type CheckGenerationJobStatusData = {
  checkGenerationJobStatus: {
    id: string;
    status: InterviewImageStatus | null;
    imageURL: string | null;
  } | null;
};

const toFileNameSlug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'pick';

export type SharePickModalProps = {
  open: boolean;
  matchup: Matchup;
  results: MatchupResults | null;
  streamName: string;
  onClose: () => void;
};

const SharePickModal = ({
  open,
  matchup,
  results,
  onClose,
}: SharePickModalProps): JSX.Element | null => {
  const cornerA = getCornerCompetitor(matchup, MatchupCorner.A);
  const cornerB = getCornerCompetitor(matchup, MatchupCorner.B);
  const breakdown = getVoteBreakdown(
    results,
    cornerA?.id || null,
    cornerB?.id || null
  );
  const votedCompetitorId = breakdown.myVoteCompetitorId;
  const pickedName =
    votedCompetitorId === cornerA?.id
      ? getCompetitorDisplayName(cornerA)
      : getCompetitorDisplayName(cornerB);
  const fileName = `my-pick-${toFileNameSlug(pickedName)}.png`;

  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [imageURL, setImageURL] = useState<string | null>(null);
  const attemptsRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const [triggerGraphic] = useMutation<{
    triggerMatchupSharePickGraphic: string;
  }>(TRIGGER_MATCHUP_SHARE_PICK_GRAPHIC);

  const startGeneration = useCallback(async () => {
    if (!votedCompetitorId) {
      return;
    }

    attemptsRef.current = 0;
    setImageURL(null);
    setStatus('generating');

    try {
      const { data } = await triggerGraphic({
        variables: { input: { matchupId: matchup.id, votedCompetitorId } },
      });

      if (!mountedRef.current) {
        return;
      }

      const id = data?.triggerMatchupSharePickGraphic;

      if (id) {
        setJobId(id);
      } else {
        setStatus('error');
      }
    } catch {
      if (mountedRef.current) {
        setStatus('error');
      }
    }
  }, [matchup.id, votedCompetitorId, triggerGraphic]);

  // Kick off generation when the modal opens; reset everything when it closes.
  useEffect(() => {
    if (!open) {
      setStatus('idle');
      setJobId(null);
      setImageURL(null);
      attemptsRef.current = 0;
      return;
    }

    if (votedCompetitorId) {
      startGeneration();
    }
    // startGeneration is stable per (matchupId, vote); only re-run on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, votedCompetitorId]);

  useQuery<CheckGenerationJobStatusData>(CHECK_GENERATION_JOB_STATUS, {
    variables: { id: jobId },
    skip: !jobId || status !== 'generating',
    fetchPolicy: 'network-only',
    pollInterval: POLL_INTERVAL_MS,
    onCompleted: (data) => {
      const job = data?.checkGenerationJobStatus;
      attemptsRef.current += 1;

      if (job?.status === InterviewImageStatus.completed && job.imageURL) {
        setImageURL(job.imageURL);
        setStatus('ready');
      } else if (
        job?.status === InterviewImageStatus.failed ||
        attemptsRef.current >= MAX_POLL_ATTEMPTS
      ) {
        setStatus('error');
      }
    },
  });

  if (!breakdown.hasVoted) {
    return null;
  }

  const handleDownload = () => {
    if (imageURL) {
      downloadImageFromUrl(imageURL, fileName);
    }
  };

  const copyImageToClipboard = async (url: string): Promise<boolean> => {
    const clipboard = navigator.clipboard as Clipboard & {
      write?: (items: ClipboardItem[]) => Promise<void>;
    };
    const writeToClipboard = clipboard?.write?.bind(clipboard);

    if (typeof ClipboardItem === 'undefined' || !writeToClipboard) {
      return false;
    }

    await writeToClipboard([
      new ClipboardItem({
        'image/png': fetch(url).then((response) => response.blob()),
      }),
    ]);
    return true;
  };

  const handleShare = async () => {
    if (!imageURL) {
      return;
    }

    try {
      if (await copyImageToClipboard(imageURL)) {
        showToast({ message: 'Image copied to clipboard' });
        return;
      }
    } catch (error) {
      console.error('SharePickModal: image copy failed', error);
    }

    try {
      await navigator.clipboard.writeText(imageURL);
      showToast({ message: 'Link copied to clipboard' });
    } catch (error) {
      console.error('SharePickModal: link copy failed', error);
      handleDownload();
    }
  };

  const isReady = status === 'ready' && !!imageURL;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="SHARE YOUR VOTE"
      titleAlign="left"
      titleVariant="h5"
      size="small"
      compactPadding
      containerClassName={styles.modalContainer}
    >
      <div className={styles.root}>
        <div className={styles.graphic}>
          {isReady ? (
            <img className={styles.image} src={imageURL} alt="Your pick" />
          ) : status === 'error' ? (
            <div className={styles.state}>
              <Text
                variant="body1Regular16"
                color="lights-medium"
                textAlign="center"
              >
                We couldn&apos;t build your graphic. Please try again.
              </Text>
              <button
                type="button"
                className={cn(styles.action, styles.actionSecondary)}
                onClick={startGeneration}
              >
                <span className={styles.actionLabel}>Retry</span>
              </button>
            </div>
          ) : (
            <div className={styles.state}>
              <Loader loading size="large" color="harvest-gold" />
              <Text
                variant="body1Regular16"
                color="lights-medium"
                textAlign="center"
              >
                Generating your graphic…
              </Text>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={cn(styles.action, styles.actionSecondary)}
            onClick={handleDownload}
            disabled={!isReady}
          >
            <MillionsIcon name="download" size={24} />
            <span className={styles.actionLabel}>Download</span>
          </button>
          <button
            type="button"
            className={cn(styles.action, styles.actionPrimary)}
            onClick={handleShare}
            disabled={!isReady}
          >
            <MillionsIcon name="share" size={24} />
            <span className={styles.actionLabel}>Share</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SharePickModal;
